import { errorMessage } from "@workspace/domain/src/utils";
import dayjs from "@workspace/domain/src/utils/dayjs";
import Decimal from "decimal.js";
import * as z from "zod";

const DiscountCodeSchema = z.object({
  id: z.string(),

  code: z.string(),
  type: z.enum(["PERCENTAGE", "FIXED"]),
  value: z.number(),
  usageLimit: z.number().nullable(),
  expiresAt: z.date().nullable(),
  newOnly: z.boolean().default(false),
  tripIds: z.array(z.string()),

  agencyId: z.string(),

  numberOfTrips: z.number(),
  numberOfReservations: z.number(),

  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

const SortBy = [
  "code",
  "type",
  "usageLimit",
  "expiresAt",
  "newOnly",
  "createdAt",
  "reservations",
  "tripDiscountCodes",
] as const;
const SortOrder = ["asc", "desc"] as const;

const GetSchema = z.object({
  id: z.string(),
});

const GetAllSchema = z
  .object({
    page: z.number(),
    limit: z.number(),

    sortBy: z.enum(SortBy),
    sortOrder: z.enum(SortOrder),
  })
  .extend(
    z
      .object({
        search: z.string(),

        valueMin: z.number().transform((val) => new Decimal(val).times(100).toNumber()),
        valueMax: z.number().transform((val) => new Decimal(val).times(100).toNumber()),

        type: z.enum(["PERCENTAGE", "FIXED"]),
        newOnly: z.boolean(),

        usageLimitMin: z.number(),
        usageLimitMax: z.number(),

        expiresAtMin: z.union([z.iso.datetime().transform((date) => dayjs(date).toDate()), z.date()]),
        expiresAtMax: z.union([z.iso.datetime().transform((date) => dayjs(date).toDate()), z.date()]),
      })
      .partial().shape,
  );

const BaseSchema = z
  .object({
    code: z
      .string(errorMessage("Code is required", "Code is invalid"))
      .nonempty("Code is required")
      .max(255, "Code must be less than 255 characters")
      .toUpperCase(),
    type: z.enum(["PERCENTAGE", "FIXED"], errorMessage("Type is required", "Type is invalid")),
    value: z.number(errorMessage("Value is required", "Value is invalid")).min(1, "Value must be greater than 0"),
    usageLimit: z
      .number(errorMessage("Max uses is required", "Max uses is invalid"))
      .min(0, "Max uses must be at least 0")
      .optional(),
    expiresAt: z.date(errorMessage("Expires at is required", "Expires at is invalid")).optional(),
    newOnly: z.boolean(errorMessage("First time only is required", "First time only is invalid")).optional(),
    tripIds: z.array(z.string()),
  })
  .refine((data) => (data.type === "PERCENTAGE" ? data.value <= 100 : true), {
    path: ["value"],
    message: "Value must be between 0 and 100 for percentage type",
  });

const CreateSchema = BaseSchema;

const UpdateSchema = BaseSchema.extend({
  id: z.string(),
});

export const AdminDiscountCode = {
  DiscountCode: DiscountCodeSchema,
  Get: GetSchema,
  GetAll: GetAllSchema,
  Create: CreateSchema,
  Update: UpdateSchema,
  SortBy: SortBy,
  SortOrder: SortOrder,
};

export type AdminDiscountCode = {
  DiscountCode: z.infer<typeof DiscountCodeSchema>;
  Get: z.infer<typeof GetSchema>;
  GetAll: z.infer<typeof GetAllSchema>;
  Create: z.infer<typeof CreateSchema>;
  Update: z.infer<typeof UpdateSchema>;
  SortBy: (typeof SortBy)[number];
  SortOrder: (typeof SortOrder)[number];
};
